import { GlobalStateController } from "bai-react-global-state";
import firebase from "firebase/compat/app";
import * as flexsearch from "flexsearch";
import _ from "lodash";
import React from "react";

import { search as searchApi } from "apis-v2/callables";
import { presentations as presentationsApi } from "apis/callables";
import {
    PresentationPrivacyType,
    PusherEventType,
    TaskState,
    THUMBNAIL_SIZES
} from "common/constants";
import { fetchCompressedIndexData, getEncoderOptions, search } from "common/flexsearch";
import {
    GET_DEFAULT_RESTRICTED_PERMISSIONS_ACCESS,
    IPresentation,
    IPresentationMetadata,
    IPresentationStorageModel,
    ISlide,
    IWorkspacePermissionAccess,
    IWorkspaceResourcePresentation,
    IWorkspaceResourcePresentationTemplate,
    WorkspaceResourceType
} from "common/interfaces";
import { IPresentationsSearchDocument } from "common/interfaces/search";
import { getSequentialRunner, SequentialRunner } from "common/utils/runSequentially";
import { withMaxConcurrency } from "common/utils/withMaxConcurrency";
import { appVersion, serverContext } from "js/config";
import WorkspaceController, {
    getUserPermissionsToResourceFolder,
    WorkspaceControllerState
} from "js/controllers/WorkspaceController";
import AppController from "js/core/AppController";
import getLogger, { LogGroup } from "js/core/logger";
import { ds } from "js/core/models/dataService";
import { getPresentation, getPresentationPreloaded, getTemplateSlideData, Presentation } from "js/core/models/presentation";
import { SharedTheme } from "js/core/models/sharedTheme";
import { Theme } from "js/core/models/theme";
import Thumbnails from "js/core/models/thumbnails";
import { User } from "js/core/models/user";
import { requestExportCache } from "js/core/services/exportCache";
import pusher, { ExtendedChannel } from "js/core/services/pusher";
import * as gDrive from "js/core/utilities/gDrive";
import uniqueName from "js/core/utilities/uniqueName";
import { downloadFromUrl, filenameForExport, trackActivity } from "js/core/utilities/utilities";
import { auth } from "js/firebase/auth";
import { app } from "js/namespaces";
import {
    ShowConfirmationDialog,
    ShowDialog,
    ShowErrorDialog,
    ShowMessageDialog,
    ShowWarningDialog
} from "js/react/components/Dialogs/BaseDialog";
import ProgressDialog from "js/react/components/Dialogs/ProgressDialog";
import Spinner from "js/react/components/Spinner";
import withProgressDialog from "../core/utilities/withProgressDialog";

const logger = getLogger(LogGroup.LIBRARY);

export interface PresentationLibraryControllerState {
    presentations: PresentationLibraryPresentation[];
    filteredPresentations: PresentationLibraryPresentation[];
    filter: PresentationLibraryFilter;
    filterMetadata: PresentationLibraryFilterMetadata;
    sort: PresentationLibrarySort;
    viewType: "grid" | "list";
    isInitialized: boolean;
    initializeError: Error | null;
    searchQuery: string;
    searchResults: IPresentationsSearchDocument[];
    isPerformingSearch: boolean;
    isLoadingPresentations: boolean;
}

export interface PresentationLibraryPresentation extends IPresentationMetadata {
    getThumbnailUrl: (size?: "small" | "large") => Promise<string>;
    dirty?: boolean;
}

export interface PresentationLibraryFilter {
    type: PresentationFilterType;
    folderId?: string;
    subFolderId?: string;
}

export interface PresentationLibraryFilterMetadata {
    permissions: IWorkspacePermissionAccess;
}

export interface PresentationLibrarySort {
    field: string;
    reverse: boolean;
}

export enum PresentationFilterType {
    ALL_PRESENTATIONS = "all_presentations",
    RECENT = "recent",
    OWNED_BY_ME = "owned_by_me",
    SHARED_WITH_ME = "shared_with_me",
    VIEW_ONLY = "view_only",
    FOLDER = "folder",
    TEAM_FOLDER = "team_folder",
    TRASH = "trash"
}

const initialState: PresentationLibraryControllerState = {
    isInitialized: false,
    initializeError: null,
    presentations: [],
    filteredPresentations: [],
    filter: {
        type: PresentationFilterType.ALL_PRESENTATIONS
    },
    filterMetadata: {
        permissions: GET_DEFAULT_RESTRICTED_PERMISSIONS_ACCESS()
    },
    sort: {
        field: "modifiedAt",
        reverse: true
    },
    viewType: "grid",
    searchQuery: "",
    searchResults: [],
    isPerformingSearch: false,
    isLoadingPresentations: false
};

class PresentationLibraryController extends GlobalStateController<PresentationLibraryControllerState> {
    protected _pusherChannel: ExtendedChannel;
    protected _firebaseUser: firebase.User;
    protected _runSequentially: SequentialRunner;
    protected _unsubscribeWorkspaceControllerStateDidUpdate: () => void;
    protected _index: flexsearch.Document<IPresentationsSearchDocument>;
    protected _indexPromise: Promise<void>;

    public get isInitialized() {
        return this._state.isInitialized;
    }

    public get filter() {
        return this._state.filter;
    }

    public get filterMetadata() {
        return this._state.filterMetadata;
    }

    get workspaceId() {
        return WorkspaceController.workspaceId;
    }

    constructor() {
        super(_.cloneDeep(initialState));
        this._runSequentially = getSequentialRunner();
    }

    //// Internals and state management ////
    protected _stateDidUpdate(prevState: PresentationLibraryControllerState): void {
        const { sort, viewType, filter, presentations, searchResults, searchQuery, isInitialized } = this._state;

        if (!isInitialized) {
            return;
        }

        if (
            !_.isEqual(prevState.presentations, presentations) ||
            !_.isEqual(prevState.filter, filter) ||
            !_.isEqual(prevState.sort, sort) ||
            !_.isEqual(prevState.searchResults, searchResults)
        ) {
            this._setFilteredPresentations();
        }

        if (!_.isEqual(prevState.sort, sort) || !_.isEqual(prevState.viewType, viewType)) {
            this._updateUserLibrarySettings();
        }

        if (!_.isEqual(prevState.searchQuery, searchQuery)) {
            this._performSearch(prevState.searchQuery);
        }

        if (!_.isEqual(prevState.filter, filter) || (!prevState.isInitialized && isInitialized)) {
            this._setFilterMetadata();
        }
    }

    protected async _initialize(initialStateValues: Partial<PresentationLibraryControllerState>) {
        return this._runSequentially(async () => {
            try {
                logger.info("[PresentationLibraryController] _initialize()");

                if (this._state.isInitialized) {
                    logger.info("[PresentationLibraryController] _initialize() resetting state");
                    await this._reset(false);
                } else {
                    // Subscribe to auth only once
                    auth().onAuthStateChanged(this._onAuthStateChanged);
                }

                await this._updateState({
                    ...initialStateValues,
                    isLoadingPresentations: true
                });

                await this._setPresentations(false);

                await this._setFilteredPresentations(false);

                await this._subscribeToPusherChannel();

                this._unsubscribeWorkspaceControllerStateDidUpdate = WorkspaceController.onStateDidUpdate(this._onWorkspaceControllerStateDidUpdate);

                await this._updateState({ isInitialized: true, isLoadingPresentations: false });

                const { presentations } = this._state;
                if (presentations.every(p => p.isFromRecommendedTemplate)) {
                    // Reporting the experiment group assignment
                    // variant-a is the 100% rollout group now for Pro individual plan
                    trackActivity("ExperimentGroup", "Assigned", "variant-a", null, {
                        experiment_id: "5FC18A79E182FE7C764425D4F852F501",
                        experiment_group_assignment: "variant-a"
                    });
                }
            } catch (err) {
                logger.error(err, "[PresentationLibraryController] initialize()");

                this._updateState({ isInitialized: false, initializeError: err });
            }
        });
    }

    /**
     * Wraps the given component with a state injector that renders the component only if the controller
     * is initialized.
     *
     * !! THIS WRAPPER WILL *NOT* INITIALIZE THE CONTROLLER IF IT'S NOT ALREADY INITIALIZED !!
     */
    public withInitializedState<T extends React.ComponentType<any>>(Component: T, PreloadComponent: React.ComponentType<any> = Spinner, ErrorComponent: React.ComponentType<any> = () => null) {
        function Wrapper(props: React.ComponentProps<T>) {
            const { isInitialized, initializeError } = props;

            if (initializeError) {
                return <ErrorComponent {...props} />;
            }

            if (!isInitialized) {
                return <PreloadComponent {...props} />;
            }

            return <Component {...props} />;
        }

        return this.withState(Wrapper);
    }

    protected _onWorkspaceControllerStateDidUpdate = (prev: WorkspaceControllerState, curr: WorkspaceControllerState) => {
        if (!curr.initialized) {
            // Won't reset ourselves, just wait for the WorkspaceController to initialize itself back (potentially with a different workspace)
            return;
        }

        if (!_.isEqual(prev.resources, curr.resources) || prev.workspace?.id !== curr.workspace.id) {
            // Clear filteredPresentations immediately to prevent showing stale data from previous workspace
            // Set loading state while we fetch new presentations for the workspace
            if (prev.workspace?.id !== curr.workspace.id) {
                this._updateState({
                    filteredPresentations: [],
                    isLoadingPresentations: true
                });
            }
            this._setPresentations()
                .then(() => {
                    this._setFilteredPresentations();
                })
                .finally(() => {
                    this._updateState({
                        isLoadingPresentations: false
                    });
                });
        }

        if (!_.isEqual(prev.resourceFolders, curr.resourceFolders)) {
            const { filter } = this._state;

            if (filter.type === PresentationFilterType.TEAM_FOLDER && filter.subFolderId && !curr.resourceFolders.some(f => f.id === filter.subFolderId)) {
                this._updateState({
                    filter: {
                        ...filter,
                        subFolderId: null
                    }
                });
            } else if (filter.type === PresentationFilterType.TEAM_FOLDER && !filter.subFolderId && !curr.resourceFolders.some(f => f.id === filter.folderId)) {
                this._updateState({
                    filter: {
                        type: PresentationFilterType.ALL_PRESENTATIONS
                    }
                });
            } else if (filter.type === PresentationFilterType.FOLDER && !curr.resourceFolders.some(f => f.id === filter.folderId)) {
                this._updateState({
                    filter: {
                        type: PresentationFilterType.ALL_PRESENTATIONS
                    }
                });
            }
        }
    }

    protected async _setFilterMetadata() {
        return this._runSequentially(async () => {
            try {
                const { filter } = this._state;

                if (filter.type !== PresentationFilterType.TEAM_FOLDER) {
                    await this._updateState({ filterMetadata: { permissions: GET_DEFAULT_RESTRICTED_PERMISSIONS_ACCESS() } });
                    return;
                }

                const permissions = await getUserPermissionsToResourceFolder({
                    workspaceId: this.workspaceId,
                    folderId: filter.folderId
                });
                await this._updateState({ filterMetadata: { permissions } });
            } catch (err) {
                logger.error(err, "[PresentationLibraryController] _setFilterMetadata()");
                await this._updateState({ filterMetadata: { permissions: GET_DEFAULT_RESTRICTED_PERMISSIONS_ACCESS() } });
            }
        });
    }

    protected async _updateUserLibrarySettings() {
        const { viewType, sort } = this._state;

        const user = await this._getUserModel();

        const librarySettings = _.cloneDeep(user.get("librarySettings") ?? {});
        librarySettings.viewType = viewType;
        if (sort.field !== "relevance") {
            librarySettings.sortField = sort.field;
            librarySettings.sortReverse = sort.reverse;
        }

        user.update({ librarySettings });
        await user.updatePromise;
    }

    protected _onAuthStateChanged = (user: firebase.User | null) => {
        const { isInitialized } = this._state;
        if (isInitialized && this._firebaseUser?.uid !== user?.uid) {
            // Force reset when user changes
            this._reset();
        }

        this._firebaseUser = user;
    }

    protected _setPresentations(syncActions: boolean = true) {
        const setPresentations = async () => {
            try {
                const { presentations: currentPresentations } = this._state;

                const currentPresentationIds = currentPresentations.map(p => p.id);

                const resourceIds = WorkspaceController.resources.filter(r => r.resourceType === WorkspaceResourceType.PRESENTATION).map(r => r.resourceId);

                const addedPresentationIds = resourceIds.filter(id => !currentPresentationIds.includes(id));
                const existingPresentationIds = resourceIds.filter(id => currentPresentationIds.includes(id));

                const hasOnlyRecommendedTemplates = currentPresentations.every(p => p.isFromRecommendedTemplate);
                if (hasOnlyRecommendedTemplates && !addedPresentationIds.length && existingPresentationIds.length) {
                    // If there are no new presentations and all the presentations are recommended templates, then we don't need to refresh the state
                    return;
                }

                const addedPresentationsMetadata = await presentationsApi.getPresentationsMetadata({ presentationIds: addedPresentationIds, workspaceId: this.workspaceId });

                let presentations: PresentationLibraryControllerState["presentations"] = [
                    ...currentPresentations.filter(p => existingPresentationIds.includes(p.id)),
                    ...addedPresentationsMetadata.map(metadata => this._getPresentationFromMetadata(metadata))
                ];

                await this._updateState({ presentations });
            } catch (err) {
                logger.error(err, "[PresentationLibraryController] _setPresentations()");

                throw err;
            }
        };

        if (!syncActions) {
            return setPresentations();
        }

        return this._runSequentially(setPresentations);
    }

    protected _reloadMetadata(presentationIds: string[]) {
        return this._runSequentially(async () => {
            const metadata = await presentationsApi.getPresentationsMetadata({ presentationIds, workspaceId: this.workspaceId });
            await this._updateState(({ presentations, ...rest }) => ({
                ...rest,
                presentations: presentations.map(p => metadata.some(m => m.id === p.id) ? this._getPresentationFromMetadata(metadata.find(m => m.id === p.id)) : p)
            }));
        });
    }

    protected async _subscribeToPusherChannel() {
        this._pusherChannel = await pusher.subscribe(`private-user-presentation-updates-${auth().currentUser.uid}`);
        this._pusherChannel.bindChunked(PusherEventType.DATA_RECORD_UPDATED, this._onPusherEvent);
    }

    protected _onPusherEvent = (payload: { presentationId: string, update: Partial<IPresentation> & { lastActivity?: { userId?: string, userName?: string, activity: string, createdAt: number } }, permissions: { owner: boolean, write: boolean, read: boolean }, triggeredAt: number }) => {
        this._runSequentially(async () => {
            const { presentationId, update, permissions, triggeredAt } = payload;
            const { presentations } = this._state;

            const index = presentations.findIndex(p => p.id === presentationId);
            if (index === -1) {
                return;
            }

            const currentPresentation = presentations[index];

            if (triggeredAt < currentPresentation.metadataGeneratedAt) {
                return;
            }

            const updatedMetadata: IPresentationMetadata = {
                ..._.omit(currentPresentation, ["getThumbnailUrl"]),
                permissions
            };

            const directlyTranslatedKeys = ["name", "createdAt", "modifiedAt", "trashedAt", "firstSlideModifiedAt", "isTemplate", "isPublished", "ratedByUsers", "lastActivity"];
            directlyTranslatedKeys.forEach(key => {
                if (key in update) {
                    updatedMetadata[key] = update[key];
                }
            });
            if ("userId" in update) {
                updatedMetadata.ownerUid = update.userId;
            }
            if ("softDeletedAt" in update) {
                updatedMetadata.trashedAt = update.softDeletedAt;
            }
            if ("slideRefs" in update) {
                const slideRefs = (update.slideRefs || {}) as Record<string, number>;
                Object.entries(slideRefs).forEach(([slideId, slideIndex]) => {
                    if (slideIndex === null) {
                        delete slideRefs[slideId];
                    }
                });
                updatedMetadata.slideIds = Object.entries(slideRefs).sort(([slideIdA, slideIndexA], [slideIdB, slideIndexB]) => slideIndexA - slideIndexB).map(([slideId, slideIndex]) => slideId);
                updatedMetadata.slideCount = updatedMetadata.slideIds.length;
            }
            if ("finishedEditingAt" in update) {
                updatedMetadata.modifiedAt = Math.max(updatedMetadata.modifiedAt, update.finishedEditingAt);
            }

            const presentation = this._getPresentationFromMetadata(updatedMetadata);

            await this._updateState(state => ({
                ...state,
                presentations: state.presentations.map(p => p.id === presentation.id ? presentation : p)
            }));
        });
    };

    protected _getPresentationFromMetadata(metadata: IPresentationMetadata): PresentationLibraryPresentation {
        // Pin scalars to protect against mutations
        const firstSlideId = metadata.slideIds[0];
        const firstSlideModifiedAt = metadata.firstSlideModifiedAt;
        const presentationId = metadata.id;

        const getThumbnailUrl = (size: "small" | "large" = "small") => {
            return Thumbnails.getSignedUrlAndLoad(firstSlideId, firstSlideModifiedAt, presentationId, THUMBNAIL_SIZES[size].suffix, 0, false);
        };

        return {
            ...metadata,
            getThumbnailUrl
        };
    }

    protected _setFilteredPresentations(syncActions: boolean = true) {
        const setFiltered = async () => {
            const { presentations, filter, sort, searchQuery, searchResults, filteredPresentations: currentFilteredPresentations } = this._state;

            let filteredPresentations = presentations
                .filter(presentation => !presentation.isTemplate)
                .filter(presentation => presentation.workspaceId === this.workspaceId);

            filteredPresentations = (() => {
                if (filter.type === PresentationFilterType.TRASH) {
                    return filteredPresentations.filter(p => p.trashedAt);
                }

                filteredPresentations = filteredPresentations.filter(p => !p.trashedAt);

                if (filter.type === PresentationFilterType.ALL_PRESENTATIONS) {
                    return [...filteredPresentations];
                }

                if (filter.type === PresentationFilterType.RECENT) {
                    const recentPresentations = AppController.user.getRecentPresentations().reverse();

                    return recentPresentations.map(id => filteredPresentations.find(p => p.id === id))
                        .filter(Boolean)
                        // Max 12 recent presentations to display
                        .slice(0, 12);
                }

                if (filter.type === PresentationFilterType.OWNED_BY_ME) {
                    return filteredPresentations.filter(p => p.permissions.owner);
                }

                if (filter.type === PresentationFilterType.SHARED_WITH_ME) {
                    return filteredPresentations.filter(p => !p.permissions.owner);
                }

                if (filter.type === PresentationFilterType.VIEW_ONLY) {
                    return filteredPresentations.filter(p => p.permissions.read && !p.permissions.write);
                }

                if (filter.type === PresentationFilterType.FOLDER || filter.type === PresentationFilterType.TEAM_FOLDER) {
                    const folderId = filter.subFolderId ?? filter.folderId;
                    const resourcesInFolder = WorkspaceController.resources.filter(r => r.folderIds.includes(folderId));
                    return filteredPresentations.filter(presentation => resourcesInFolder.some(r => r.resourceId === presentation.id && r.resourceType === WorkspaceResourceType.PRESENTATION));
                }
            })();

            filteredPresentations = _.orderBy(filteredPresentations, "modifiedAt", "desc");

            if (searchQuery) {
                filteredPresentations = filteredPresentations
                    .filter(presentation => searchResults.some(r => r.presentationId === presentation.id));

                if (sort.field === "relevance") {
                    filteredPresentations = filteredPresentations
                        .sort((a, b) => {
                            const idxA = searchResults.findIndex(r => r.presentationId === a.id);
                            const idxB = searchResults.findIndex(r => r.presentationId === b.id);

                            if (sort.reverse) {
                                return idxB - idxA;
                            }

                            return idxA - idxB;
                        });
                } else {
                    filteredPresentations = _.orderBy(filteredPresentations, sort.field, sort.reverse ? "desc" : "asc");
                }
            } else {
                filteredPresentations = _.orderBy(filteredPresentations, sort.field, sort.reverse ? "desc" : "asc");
            }

            if (_.isEqual(
                filteredPresentations.map(p => _.omit(p, ["getThumbnailUrl"])),
                currentFilteredPresentations.map(p => _.omit(p, ["getThumbnailUrl"])))
            ) {
                return;
            }

            await this._updateState({ filteredPresentations });
        };

        if (!syncActions) {
            return setFiltered();
        }

        return this._runSequentially(setFiltered);
    }

    protected _performSearch(prevSearchQuery: string) {
        return this._runSequentially(async () => {
            const { searchQuery, sort } = this._state;

            if (!searchQuery) {
                const stateUpdates = {
                    searchResults: [],
                    sort
                };

                if (sort.field === "relevance") {
                    // Ensure the sort is reset to modifiedAt when searchQuery is empty
                    stateUpdates.sort = {
                        field: "modifiedAt",
                        reverse: true
                    };
                }

                await this._updateState(stateUpdates);
                return;
            }

            await this._updateState({ isPerformingSearch: true });

            await this.ensureSearchIndexPrepared();

            const searchResults = search({
                index: this._index,
                query: searchQuery
            });

            const stateUpdates = {
                isPerformingSearch: false,
                searchResults: searchResults.slice(0, 300), // Limit to top 300 records
                sort
            };

            if (!prevSearchQuery) {
                // Switch to relevance sort when started searching
                stateUpdates.sort = {
                    field: "relevance",
                    reverse: false
                };
            }

            await this._updateState(stateUpdates);
        });
    }

    protected _generatePresentationName() {
        const presentationResources = WorkspaceController.resources
            .filter(r =>
                r.resourceType === WorkspaceResourceType.PRESENTATION ||
                r.resourceType === WorkspaceResourceType.PRESENTATION_TEMPLATE
            ) as (IWorkspaceResourcePresentation | IWorkspaceResourcePresentationTemplate)[];

        const maxNumber = presentationResources.reduce((max, resource) => {
            if (resource.metadata?.name?.startsWith("Untitled")) {
                const numbers = resource.metadata.name.match(/\d+/g);
                if (numbers) {
                    const number = parseInt(numbers[0]);
                    if (!isNaN(number)) {
                        return Math.max(max, number);
                    }
                }
            }

            return max;
        }, 0);

        return `Untitled #${maxNumber + 1}`;
    }

    protected async _createPresentation(options: {
        workspaceId: string;
        presentation: Partial<IPresentation>;
        theme?: typeof Theme | typeof SharedTheme;
        slides?: (Partial<ISlide> & { template_id: string })[];
    }) {
        const {
            workspaceId,
            presentation: initialData,
            theme,
            slides = [],
        } = options;

        if (slides.length === 0 && Object.keys(initialData.slideRefs || {}).length === 0) {
            throw new Error("At least one slide is required");
        }

        const uid = auth().currentUser.uid;

        const playerSettingsDefaults = _.cloneDeep(WorkspaceController.workspace.player.settings);

        const presentationData: Partial<IPresentation> = {
            ...playerSettingsDefaults,
            ...initialData,
            sharedThemeId: null,
            userId: uid,
            isTemplate: false
        };

        if (theme && theme instanceof SharedTheme) {
            presentationData.sharedThemeId = theme.id;
        } else if (theme) {
            Object.assign(presentationData, app.themeManager.getPresentationUpdates(theme));
        }

        const slidesData: Partial<ISlide>[] = [];
        if (slides.length > 0) {
            await Promise.all(slides.map(async slideData => {
                slideData = await getTemplateSlideData(slideData.template_id, {
                    ...slideData,
                    version: appVersion
                });

                slidesData.push(slideData);
            }));
        }

        const presentation = await presentationsApi.createPresentationWithSlidesAndResource({
            workspaceId,
            presentation: presentationData,
            slides: slidesData
        });

        const presentationModel = await getPresentationPreloaded(presentation, workspaceId, { permission: "write", autoSync: true });

        const eventProps = {
            "presentation_id": presentationModel.id,
            "current_slide_count": this._getSlideCount(workspaceId) + slides.length,
            "slides_created": slides.length,
            "workspace_id": workspaceId
        };
        trackActivity("Presentation", "New", null, null, eventProps, { audit: true });

        // Fire and forget
        (async () => {
            const user = await this._getUserModel();
            if (user.get("isGDriveEnabled")) {
                const gDriveFolderId = new URL(window.location.href).searchParams.get("gDriveFolderId");
                gDrive.savePresentation(presentationModel.id, gDriveFolderId)
                    .catch(err => {
                        logger.error(err, "PresentationLibraryController gDrive.savePresentation() failed", { presentationId: presentationModel.id, gDriveFolderId });
                    });
            }
        })().catch(err => {
            logger.error(err, "[PresentationLibraryController] _createPresentation() failed to save presentation to GDrive", { presentationId: presentationModel.id });
        });

        // Ensure the resource is added to the workspace controller state (in case the real time update comes with a delay)
        WorkspaceController.forceRefreshWorkspaceResources([{ resourceId: presentationModel.id, resourceType: WorkspaceResourceType.PRESENTATION }])
            .catch(err => {
                logger.error(err, "[PresentationLibraryController] _createPresentation() failed to refresh workspace resources", { presentationId: presentationModel.id });
            });

        return presentationModel;
    }

    protected async _movePresentation(presentation: typeof Presentation, libraryFilter: PresentationLibraryFilter) {
        if (libraryFilter.type === PresentationFilterType.TEAM_FOLDER || libraryFilter.type === PresentationFilterType.FOLDER) {
            const folderId = libraryFilter.subFolderId ?? libraryFilter.folderId;

            try {
                await WorkspaceController.updateWorkspaceResourceFolderResources({
                    folderId,
                    update: {
                        add: [{
                            resourceId: presentation.id,
                            resourceType: WorkspaceResourceType.PRESENTATION,
                            permissions: { read: { object: true, permissions: true }, write: { object: true, permissions: true } }
                        }],
                    }
                });
            } catch (err) {
                logger.error(err, "PresentationLibraryController _movePresentation() failed", { presentationId: presentation.id, libraryFilter });

                ShowErrorDialog({
                    title: "Oops!",
                    message: "Failed to move presentation to folder",
                    acceptCallback: () => { }
                });
            }
        }
    }

    protected async _getUserModel() {
        if (AppController.user?.isLoaded) {
            return AppController.user;
        }

        const uid = auth().currentUser.uid;
        const user = new User({ id: uid }, { autoLoad: false });
        await user.loadMinimum();
        return user;
    }

    protected _getSlideCount(workspaceId: string) {
        const { presentations } = this._state;

        return presentations.reduce((count, presentation) => {
            if (presentation.workspaceId === workspaceId) {
                return count + presentation.slideCount;
            }

            return count;
        }, 0);
    }

    protected async _reset(syncActions: boolean = true): Promise<void> {
        logger.info("[PresentationLibraryController] reset()");

        const reset = async () => {
            try {
                this.disposeSearchIndex();

                await this._updateState(_.cloneDeep(initialState));

                if (this._unsubscribeWorkspaceControllerStateDidUpdate) {
                    this._unsubscribeWorkspaceControllerStateDidUpdate();
                    this._unsubscribeWorkspaceControllerStateDidUpdate = null;
                }

                if (this._pusherChannel) {
                    this._pusherChannel.unbind(PusherEventType.DATA_RECORD_UPDATED, this._onPusherEvent);
                    if (!this._pusherChannel.isInUse) {
                        pusher.unsubscribe(this._pusherChannel.name);
                    }
                    this._pusherChannel = null;
                }
            } catch (err) {
                logger.error(err, "[PresentationLibraryController] _reset()");

                throw err;
            }
        };

        if (!syncActions) {
            return reset();
        }

        return this._runSequentially(reset);
    }

    //// Initialize and reset methods ////
    public initialize(initialStateValues: Partial<PresentationLibraryControllerState> & { workspaceId: string }) {
        return this._initialize(initialStateValues);
    }

    public reset() {
        return this._reset();
    }

    //// Search ////
    public async ensureSearchIndexPrepared() {
        if (this._indexPromise) {
            return this._indexPromise;
        }

        this._indexPromise = (async () => {
            const { indexConfig, indexFileReadUrl } = await searchApi.getPresentationsSearchIndex({
                workspaceId: this.workspaceId,
                locale: serverContext.locale ?? "en"
            });

            const indexData = await fetchCompressedIndexData(indexFileReadUrl);

            this._index = new flexsearch.Document<IPresentationsSearchDocument>({
                encoder: getEncoderOptions(serverContext.locale ?? "en"),
                ...indexConfig,
            });
            indexData.forEach(({ key, data }) => this._index.import(key, data));
        })();
    }

    public async disposeSearchIndex() {
        this._index = null;
        this._indexPromise = null;
    }

    //// Dummy state getting methods ////
    public getSlideCount(workspaceId: string) {
        return this._getSlideCount(workspaceId);
    }

    public getPresentations(workspaceId: string) {
        return this._state.presentations.filter(p => p.workspaceId === workspaceId);
    }

    ////// Dummy state updating methods ////
    public async setFilter(filter: PresentationLibraryFilter) {
        await this._updateState({ filter });
    }

    public async setSort(sort: PresentationLibrarySort) {
        await this._updateState({ sort });
    }

    public async setViewType(viewType: "grid" | "list") {
        await this._updateState({ viewType });
    }

    public async setSearchQuery(searchQuery: string) {
        await this._updateState({ searchQuery });
    }

    //// Helpers to trigger faster updates ////
    public async forceRefreshPresentations(presentationIds: string[]) {
        return this._reloadMetadata(presentationIds);
    }

    public async markPresentationsDirty(presentationIds: string[]) {
        await this._updateState(state => ({
            ...state,
            presentations: state.presentations.map(p => presentationIds.includes(p.id) ? { ...p, dirty: true } : p)
        }));
    }

    //// Logic-heavy methods ////
    public async createPresentation(options: {
        workspaceId: string;
        theme?: typeof Theme | typeof SharedTheme;
        name?: string;
        isTemplate?: boolean;
        slides?: (Partial<ISlide> & { template_id: string })[];
        metadata?: Record<string, any>;
        libraryFilter?: PresentationLibraryFilter;
    }) {
        const {
            workspaceId,
            theme = ds.builtInThemes.sample(),
            slides = [],
            name = this._generatePresentationName(),
            isTemplate = false,
            metadata = {},
            libraryFilter
        } = options;

        if (slides.length === 0) {
            slides.push({ template_id: "title" });
        }

        const initialData: Pick<IPresentation, "name" | "orgId" | "metadata" | "createdAt" | "modifiedAt" | "isTemplate"> = {
            name,
            orgId: workspaceId,
            metadata,
            createdAt: Date.now(),
            modifiedAt: Date.now(),
            isTemplate
        };

        const presentation = await this._createPresentation({
            workspaceId,
            presentation: initialData,
            theme,
            slides
        });

        if (libraryFilter) {
            await this._movePresentation(presentation, libraryFilter);
        }

        return presentation;
    }

    public handleDuplicate(presentationId: string) {
        withProgressDialog({
            title: "Duplicating presentation...",
            action: async () => {
                try {
                    const duplicatedPresentation = await this.duplicatePresentation({ presentationId, libraryFilter: this.filter });
                    AppController.showEditor({ presentationId: duplicatedPresentation.id });
                } catch (err) {
                    ShowErrorDialog({ title: "Unable to duplicate presentation(s)", message: err.message });
                }
            }
        });
    }

    public async duplicatePresentation(options: {
        presentationId: string;
        libraryFilter?: PresentationLibraryFilter;
    }) {
        const { presentations } = this._state;
        const { presentationId, libraryFilter } = options;

        const presentation = await getPresentation(presentationId, { permission: "read", autoSync: false });
        const workspaceId = presentation.getWorkspaceId();

        const name = uniqueName(presentation.get("name"), presentations.map(p => p.name));
        const { presentationData } = await presentationsApi.copyPresentation({
            id: presentationId,
            workspaceId: presentation.getWorkspaceId(),
            name
        });

        presentation.disconnect();

        const duplicatedPresentation = await getPresentationPreloaded(presentationData, workspaceId, { permission: "write", autoSync: true });

        if (libraryFilter) {
            await this._movePresentation(duplicatedPresentation, libraryFilter);
        }

        return duplicatedPresentation;
    }

    public async createPresentationFromTemplate(options: {
        templatePresentationId: string;
        libraryFilter?: PresentationLibraryFilter;
    }) {
        const { templatePresentationId, libraryFilter } = options;

        const duplicatedPresentation = await this.duplicatePresentation({
            presentationId: templatePresentationId,
            libraryFilter
        });

        duplicatedPresentation.update({ isTemplate: false });
        await duplicatedPresentation.updatePromise;

        return duplicatedPresentation;
    }

    public async createPresentationFromStarterTemplate(options: {
        starterTemplateId: string;
        workspaceId: string;
        libraryFilter?: PresentationLibraryFilter;
        addToRecent?: boolean;
    }) {
        const { starterTemplateId, workspaceId, libraryFilter, addToRecent = false } = options;

        const { presentationData } = await presentationsApi.copyPresentation({
            id: starterTemplateId,
            workspaceId
        });

        const duplicatedPresentation = await getPresentationPreloaded(presentationData, workspaceId, { permission: "write", autoSync: true });

        if (libraryFilter) {
            await this._movePresentation(duplicatedPresentation, libraryFilter);
        }

        if (addToRecent) {
            const user = await this._getUserModel();
            await user.addPresentationsToRecent([duplicatedPresentation.id]);
        }

        return duplicatedPresentation;
    }

    public async createPresentationFromPPTImport(options: {
        presentationId: string;
        libraryFilter?: PresentationLibraryFilter;
    }) {
        const { presentationId, libraryFilter } = options;

        const createdPresentation = await getPresentation(presentationId, { autoSync: true, permission: "write" });

        // Ensure the slide references are updated
        if (!createdPresentation.get("slideRefs")) {
            await new Promise(resolve => createdPresentation.once("change:slideRefs", resolve));
        }

        if (libraryFilter) {
            await this._movePresentation(createdPresentation, libraryFilter);
        }

        return createdPresentation;
    }

    public async trashPresentations(presentationIds: string[]) {
        const presentations = await Promise.all(presentationIds.map(id => getPresentation(id, { permission: "write", autoSync: false })));

        const disconnect = () => presentations.forEach(p => p.disconnect());

        if (presentations.some(p => !p.permissions.owner)) {
            ShowWarningDialog({
                title: "Oops!",
                message: "Presentations that are being collaborated on can not move to trash!",
                acceptCallback: () => { }
            });
            disconnect();
            return;
        }

        // This will remove all collaborators from the presentations
        await Promise.all(presentations.map(p =>
            WorkspaceController.updateWorkspaceResourceUsers({
                resourceId: p.id,
                resourceType: WorkspaceResourceType.PRESENTATION,
                update: {
                    // Replace with empty array == remove all
                    replace: []
                }
            }))
        );

        const user = await this._getUserModel();

        await Promise.all(presentations.map(async presentation => {
            const resource = WorkspaceController.resources.find(r => r.resourceId === presentation.id && r.resourceType === WorkspaceResourceType.PRESENTATION);
            if (resource?.folderIds.length) {
                await Promise.all(resource.folderIds.map(folderId =>
                    WorkspaceController.updateWorkspaceResourceFolderResources({
                        folderId,
                        update: {
                            remove: [{ resourceId: presentation.id, resourceType: WorkspaceResourceType.PRESENTATION }]
                        }
                    })
                ));
            }

            await presentation.setPrivacySetting(PresentationPrivacyType.PRIVATE);

            presentation.update({ softDeletedAt: new Date().getTime() });
            await presentation.updatePromise;

            if (user.get("isGDriveEnabled")) {
                gDrive.removePresentation(presentation.id)
                    .catch(err => {
                        logger.error(err, "PresentationLibraryController gDrive.removePresentation() failed", { presentationId: presentation.id });
                    });
            }

            const eventProps = {
                "presentation_id": presentation.id,
                "workspace_id": presentation.getWorkspaceId(),
                "library_location": this._state.filter.type,
                "current_folder_id": this._state.filter.folderId
            };
            trackActivity("Presentation", "MoveToTrash", null, null, eventProps, { audit: true });
        }));

        disconnect();

        // Fire and forget
        this._reloadMetadata(presentationIds)
            .catch(err => {
                logger.error(err, "PresentationLibraryController _reloadMetadata() failed", { presentationIds });
            });
    }

    public async recoverPresentations(presentationIds: string[]) {
        await Promise.all(presentationIds.map(async id => {
            const presentation = await getPresentation(id, { permission: "write", autoSync: false });

            if (!presentation.get("softDeletedAt")) {
                presentation.disconnect();
                return;
            }

            presentation.update({ softDeletedAt: null });
            await presentation.updatePromise;

            const eventProps = {
                "presentation_id": presentation.id,
                "library_location": this._state.filter.type,
            };
            trackActivity("Presentation", "Recovered", null, null, eventProps, { audit: true });

            const user = await this._getUserModel();
            if (user.get("isGDriveEnabled")) {
                gDrive.savePresentation(presentation.id)
                    .catch(err => {
                        logger.error(err, "PresentationLibraryController gDrive.savePresentation() failed", { presentationId: presentation.id });
                    });
            }

            presentation.disconnect();
        }));

        // Fire and forget
        this._reloadMetadata(presentationIds)
            .catch(err => {
                logger.error(err, "PresentationLibraryController _reloadMetadata() failed", { presentationIds });
            });
    }

    public async deletePresentations(presentationIds: string[]) {
        await Promise.all(presentationIds.map(async id => {
            const presentation = await getPresentation(id, { permission: "read", autoSync: false });

            await presentationsApi.deletePresentation({ id });
            await WorkspaceController.forceRefreshWorkspaceResource(id, WorkspaceResourceType.PRESENTATION);

            // Legacy analytics
            const deletedSlideCount = -Object.keys(presentation.get("slideRefs") || {}).length;
            const workspaceId = presentation.getWorkspaceId();

            const eventProps = {
                "presentation_id": id,
                "library_location": this._state.filter.type,
                "current_slide_count": this._getSlideCount(workspaceId),
                "slides_created": deletedSlideCount,
            };
            trackActivity("Presentation", "Delete", null, null, eventProps, { audit: true });
            ////
        }));
    }

    public async playPresentation(presentationId: string, goBackToLibrary: boolean = false) {
        const presentation = await getPresentation(presentationId, { permission: "read", autoSync: false });

        const entry_slide_index = 0;
        trackActivity("Presentation", "Present");
        const props = {
            initiated_via: "library",
            entry_slide_index: entry_slide_index + 1,
            presentation_id: presentation.id,
            presentation_slide_count: presentation.getSlideCount(),
            current_slide_count: this._getSlideCount(presentation.getWorkspaceId()),
            workspace_id: presentation.getWorkspaceId()
        };
        trackActivity("Presentation", "PresentOpen", null, null, props, { audit: true });

        if (window.roomID) {
            if (!presentation.get("public")) {
                if (await ShowConfirmationDialog({
                    title: "Do you want to set the presentation to public?",
                    message: "Your presentation is set to private but must be public in order to launch using remote control.",
                    okButtonLabel: "Set to public"
                })) {
                    await presentation.setPrivacySetting(PresentationPrivacyType.PUBLIC);
                }
            }
        }

        if (app.integrator) {
            app.integrator.registerPresentation(presentation);
            return;
        }

        if (app.isMobileOrTablet) {
            // To load the player UI to be compatible for mobile we need to switch the location to the player url instead
            // of playing the presentation from app.mainView.playPresentation
            let url = `/player/${presentation.id}`;
            if (window.roomID) {
                url += `?roomID=${window.roomID}&remoteRole=leader`;

                // for efficiency, trigger the registerLeader event on the pusher channel so that the clients can start loading their players immediately
                // instead of needing to wait for our player redirect to load
                const remoteSetupChannel = await pusher.subscribe(`presence-remote-control-${window.roomID}`);
                remoteSetupChannel.trigger("client-registerLeader", { presentationId: presentation.id });
            }
            window.location.href = url;
            return;
        }

        if (window.roomID) {
            // @ts-ignore
            window.remoteRole = "leader";
        }

        AppController.playPresentation({ presentationId: presentation.id, slideIndex: entry_slide_index }, { goBackToLibrary });
    }

    public async exportPresentations({
        assetType = "pdf",
        workspaceId = null,
        includeSkippedSlides = false,
    }: {
        assetType?: "pdf" | "pptx" | "zip";
        workspaceId?: string;
        includeSkippedSlides?: boolean;
    }) {
        const presentations = workspaceId ? this._state.presentations.filter(p => p.workspaceId === workspaceId) : this._state.presentations;
        if (presentations.length === 0) {
            ShowWarningDialog({
                title: "No presentations found",
                message: "Nothing to export",
                acceptCallback: () => { }
            });
            return;
        }

        const exportPresentation = (presentation: PresentationLibraryPresentation) => new Promise<string>((resolve, reject) => {
            const onTaskChanged = (task: { state: string, signedUrl: string, errorMessage: string, stateProgressPercents: number }) => {
                if (task.state === TaskState.ERROR) {
                    reject(new Error(`Export failed with message: ${task.errorMessage}`));
                    return;
                }

                if (task.state === TaskState.FINISHED) {
                    resolve(task.signedUrl);
                    return;
                }
            };

            const taskProps = {
                presentationId: presentation.id,
                assetType,
                forPrinting: false,
                includeSkippedSlides,
                pdfCompressionType: "none"
            };

            requestExportCache(onTaskChanged, taskProps)
                .catch(reject);
        });

        let shouldCancel = false;
        const progressDialog = ShowDialog(ProgressDialog, {
            title: `Exporting ${presentations.length} presentations`,
            onCancel: () => {
                shouldCancel = true;
                progressDialog.setTitle("Cancelling export...");
            }
        });

        let exportedCount = 0;
        let failedCount = 0;
        await withMaxConcurrency(presentations.map(presentation => async () => {
            if (shouldCancel) {
                return;
            }

            try {
                const signedUrl = await exportPresentation(presentation);
                if (shouldCancel) {
                    return;
                }
                const fileName = filenameForExport({ name: presentation.name, assetType });
                await downloadFromUrl(signedUrl, fileName);
                exportedCount++;
                progressDialog.setProgress(exportedCount / presentations.length * 100);
            } catch (err) {
                failedCount++;
                logger.error(err, "PresentationLibraryController exportPresentations() failed", { presentationId: presentation.id });
            }
        }), 10);

        progressDialog.props.closeDialog();

        ShowMessageDialog({
            title: "Export complete",
            message: `Exported ${exportedCount} presentations.${failedCount ? ` Failed to export ${failedCount} presentations.` : ""}${shouldCancel ? "  Export cancelled." : ""}`,
            acceptCallback: () => { }
        });
    }

    public async convertToPresentationTemplate(presentation: IPresentationStorageModel, resourceOptions?: { tags: string[], metadata: Partial<IWorkspaceResourcePresentationTemplate["metadata"]> }, openEditor?: boolean) {
        return withProgressDialog(
            {
                title: "Creating template...",
                action: async () => {
                    try {
                        await presentation.load();

                        const defaultMetadata: IWorkspaceResourcePresentationTemplate["metadata"] = {
                            description: "",
                            name: "",
                            isTemplateConfirmed: true,
                            isPublished: true,
                            ratedByUsers: [],
                            slideCount: 0,
                            sharedThemeId: null
                        };

                        const presentationMetadata: Partial<IWorkspaceResourcePresentationTemplate["metadata"]> = {
                            name: presentation.get("name"),
                            slideCount: presentation.get("slideRefs") ? Object.keys(presentation.get("slideRefs")).length : undefined,
                            sharedThemeId: presentation.get("sharedThemeId")
                        };

                        await WorkspaceController.createWorkspaceResource({
                            resource: {
                                resourceType: WorkspaceResourceType.PRESENTATION_TEMPLATE,
                                resourceId: presentation.id,
                                tags: resourceOptions?.tags ?? [],
                                folderIds: [],
                                metadata: _.defaults(resourceOptions?.metadata ?? {}, presentationMetadata, defaultMetadata)
                            },
                            defaultPermissions: "shared"
                        });

                        await presentation.reload();

                        if (openEditor) {
                            AppController.showEditor({ presentationId: presentation.id });
                        }

                        return presentation;
                    } catch (err) {
                        logger.error(err, "PresentationLibraryController convertToPresentationTemplate() failed", { presentationId: presentation.id });

                        ShowErrorDialog({
                            title: "Error",
                            message: "Failed to create template. Please try again."
                        });
                    }
                }
            }
        );
    }

    public async updatePresentationTemplate(presentation: IPresentationStorageModel, resourceOptions?: { tags: string[], metadata: Partial<Omit<IWorkspaceResourcePresentationTemplate["metadata"], "ratedByUsers">> }) {
        return withProgressDialog(
            {
                title: "Saving template...",
                action: async () => {
                    try {
                        await presentation.load();

                        await WorkspaceController.updateWorkspaceResource({
                            resourceType: WorkspaceResourceType.PRESENTATION_TEMPLATE,
                            resourceId: presentation.id,
                            tags: resourceOptions?.tags ?? undefined,
                            metadata: resourceOptions?.metadata ?? undefined
                        });

                        await presentation.reload();

                        return presentation;
                    } catch (err) {
                        logger.error(err, "PresentationLibraryController updatePresentationTemplate() failed", { presentationId: presentation.id });

                        ShowErrorDialog({
                            title: "Error",
                            message: "Failed to save template. Please try again."
                        });
                    }
                }
            }
        );
    }
}

const presentationLibraryController = new PresentationLibraryController();

// for lazy import and debug
app.presentationLibraryController = presentationLibraryController;

export default presentationLibraryController;
